import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 * @property {HTMLButtonElement | undefined} minusButton
 * @property {HTMLButtonElement | undefined} plusButton
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.#checkQuantityRules();
    this.#syncButtonState();
  }

  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement) || this.refs.plusButton?.disabled) return;

    event.preventDefault();
    this.refs.quantityInput.stepUp();
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement) || this.refs.minusButton?.disabled) return;

    event.preventDefault();
    this.refs.quantityInput.stepDown();
    this.#onQuantityChange();
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    if (event.target instanceof HTMLInputElement) {
      this.refs.quantityInput.value = event.target.value;
    }
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity change event.
   */
  #onQuantityChange() {
    const { quantityInput } = this.refs;

    this.#checkQuantityRules();
    this.#syncButtonState();
    const newValue = parseInt(quantityInput.value, 10);

    quantityInput.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine)));
  }

  /**
   * Checks the quantity rules are met
   */
  #checkQuantityRules = () => {
    const { quantityInput } = this.refs;
    const min = quantityInput.min === '' ? null : Number(quantityInput.min);
    const max = quantityInput.max === '' ? null : Number(quantityInput.max);
    const newValue = Number.parseInt(quantityInput.value, 10);

    if (Number.isNaN(newValue)) return;

    if (min !== null && !Number.isNaN(min) && newValue < min) quantityInput.value = `${min}`;
    if (max !== null && !Number.isNaN(max) && newValue > max) quantityInput.value = `${max}`;
  };

  #syncButtonState = () => {
    const { quantityInput, minusButton, plusButton } = this.refs;
    const min = quantityInput.min === '' ? null : Number(quantityInput.min);
    const max = quantityInput.max === '' ? null : Number(quantityInput.max);
    const currentValue = Number.parseInt(quantityInput.value, 10);
    const controlsDisabled = quantityInput.disabled;

    if (minusButton instanceof HTMLButtonElement) {
      minusButton.disabled = controlsDisabled || (min !== null && !Number.isNaN(min) && currentValue <= min);
    }

    if (plusButton instanceof HTMLButtonElement) {
      plusButton.disabled = controlsDisabled || (max !== null && !Number.isNaN(max) && currentValue >= max);
    }
  };

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', QuantitySelectorComponent);
}
